/**
 * Utility functions for Well Registration Totals report
 * @module wellRegistrationTotals/utils
 */

import {
  MONTH_NAMES,
  CLICKABLE_ROW_KINDS,
  BOLD_ROW_KINDS,
  BOLD_YEAR_COLUMN_KINDS,
} from "./constants";

/**
 * Check if a row kind supports drilldown click
 * @param {string} rowKind - The row_kind value from the data
 * @returns {boolean}
 */
export const isClickableRow = (rowKind) =>
  CLICKABLE_ROW_KINDS.includes(rowKind);

/**
 * Check if a row kind should render with bold styling
 * @param {string} rowKind - The row_kind value from the data
 * @returns {boolean}
 */
export const isBoldRow = (rowKind) => BOLD_ROW_KINDS.includes(rowKind);

/**
 * Check if the year column should render bold for this row kind
 * @param {string} rowKind - The row_kind value from the data
 * @returns {boolean}
 */
export const isBoldYearColumn = (rowKind) =>
  BOLD_YEAR_COLUMN_KINDS.includes(rowKind);

/**
 * Get CSS class names for a report table row
 * @param {Object} row - Row data object
 * @param {Object|null} selectedRow - Currently selected row or null
 * @returns {string} Space-separated class names
 */
/** Row kinds that render with red adjustment styling */
const ADJUSTMENT_ROW_KINDS = ["never_drilled", "plugged"];

export const getRowClassName = (row, selectedRow) => {
  const classes = [];

  if (row.row_kind === "grand_total" || row.row_kind === "adjusted_total") {
    classes.push("grand-total-row");
  } else if (row.row_kind === "year_total") {
    classes.push("subtotal-row");
  } else if (row.row_kind === "month") {
    classes.push("month-row");
  }

  if (ADJUSTMENT_ROW_KINDS.includes(row.row_kind)) {
    classes.push("adjustment-row");
  }

  if (isClickableRow(row.row_kind)) {
    classes.push("clickable-row");
  }

  if (
    selectedRow &&
    row.period_start === selectedRow.period_start &&
    row.period_end === selectedRow.period_end
  ) {
    classes.push("selected-row");
  }

  return classes.join(" ");
};

/**
 * Extract month abbreviation from row label
 * Converts "2025 - Jan" to "Jan"
 * @param {string} rowLabel - The row_label value
 * @param {string} rowKind - The row_kind value
 * @returns {string} Display label
 */
export const formatRowLabel = (rowLabel, rowKind) => {
  if (rowKind === "month") {
    const match = rowLabel.match(/^\d{4}\s*-\s*(\w{3})$/);
    if (match) return match[1];
  }
  // Strip ** prefix from adjustment rows
  if (rowLabel && rowLabel.startsWith("**")) {
    return rowLabel.replace(/^\*\*/, "");
  }
  return rowLabel;
};

/**
 * Format row_label into a descriptive title for drilldown
 * @param {string} rowLabel - The row_label value
 * @returns {string} Formatted title
 * @example
 * formatDrilldownTitle("2025 - Jan") // "Registered Wells - January 2025"
 * formatDrilldownTitle("Total 2025") // "Registered Wells - 2025"
 */
export const formatDrilldownTitle = (rowLabel) => {
  if (!rowLabel) return "Registered Wells";

  // Handle "Total YYYY" format
  if (rowLabel.startsWith("Total ")) {
    const year = rowLabel.replace("Total ", "");
    return `Registered Wells - ${year}`;
  }

  // Handle "YYYY - Mon" format
  const match = rowLabel.match(/^(\d{4})\s*-\s*(\w{3})$/);
  if (match) {
    const [, year, monthAbbr] = match;
    const monthFull = MONTH_NAMES[monthAbbr] || monthAbbr;
    return `Registered Wells - ${monthFull} ${year}`;
  }

  return `Registered Wells - ${rowLabel}`;
};

/**
 * Format a cell value that may be N/A (null)
 * @param {*} value - Cell value
 * @returns {string|number} "N/A" if null, otherwise the value or 0
 */
export const formatNullableCell = (value) => {
  if (value === null) return "N/A";
  return value || 0;
};

/**
 * Generate filename base from row label
 * @param {string} rowLabel - The row_label value
 * @returns {string} Sanitized filename base
 */
export const getFilenameBase = (rowLabel) =>
  rowLabel?.replace(/\s+/g, "-") || "drilldown";

/**
 * Prepare drilldown wells data for CSV export
 * @param {Array} wells - Array of well objects
 * @param {Array} headers - CSV header row
 * @returns {Array} CSV data array with headers
 */
export const prepareDrilldownCsvRows = (wells, headers) => {
  if (!wells || wells.length === 0) return [headers];

  const rows = wells.map((well) => [
    well.cuwcd_well_number || "-",
    well.contact_name || "-",
    well.address || "-",
    well.city || "-",
    well.zip || "-",
    well.aquifer_name || "-",
    well.well_depth_ft ? `${well.well_depth_ft}'` : "-",
    well.primary_use || "-",
    well.well_status || "-",
  ]);

  return [headers, ...rows];
};
